import { orgAPI } from "@/api/api";
import { useUpdateCanvas } from "@/api/canvas";
import { useWorkspaces } from "@/api/workspaces";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Canvas } from "@cohesive/api-client";
import { Layers, User } from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

interface MoveCanvasDialogProps {
  canvas: Canvas | null;
  onClose: () => void;
  onMoved?: (canvas: Canvas) => void;
}

export function MoveCanvasDialog({
  canvas,
  onClose,
  onMoved,
}: MoveCanvasDialogProps) {
  const open = canvas !== null;
  const { data: workspaces = [] } = useWorkspaces();
  const [targetId, setTargetId] = useState<string>("");
  const [isFetchingLatest, setIsFetchingLatest] = useState(false);

  // Reset selection when a new canvas is opened
  useEffect(() => {
    if (canvas) {
      setTargetId("");
      setIsFetchingLatest(false);
    }
  }, [canvas?.id]);

  const updateCanvas = useUpdateCanvas({
    onSuccess: (updated) => {
      toast.success("Canvas moved");
      onMoved?.(updated);
      onClose();
    },
    onError: () => toast.error("Failed to move canvas"),
  });

  const isBusy = isFetchingLatest || updateCanvas.isPending;

  // Sort: team workspaces first, private last
  const sortedWorkspaces = [...workspaces].sort((a, b) => {
    if (a.is_private === b.is_private) return a.name.localeCompare(b.name);
    return a.is_private ? 1 : -1;
  });

  // Filter out the canvas's current workspace as a destination
  const eligibleWorkspaces = sortedWorkspaces.filter(
    (ws) => ws.id !== canvas?.workspace_id,
  );

  const handleMove = async () => {
    if (!canvas || !targetId || isBusy) return;

    // Re-fetch the canvas right before the PUT so we overwrite with the
    // freshest server state instead of the potentially stale props/cache
    // (see Cursor Bugbot: "Move dialog sends stale fields").
    setIsFetchingLatest(true);
    let latest: Canvas;
    try {
      latest = await orgAPI.canvas.getById(canvas.id);
    } catch {
      toast.error("Failed to move canvas");
      setIsFetchingLatest(false);
      return;
    }
    setIsFetchingLatest(false);

    updateCanvas.mutate({
      id: canvas.id,
      data: {
        name: latest.name,
        description: latest.description,
        metadata: latest.metadata,
        workspace_id: targetId,
      },
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Move canvas</DialogTitle>
          <DialogDescription>
            Choose a workspace to move
            {canvas?.name ? ` "${canvas.name}"` : " this canvas"} to.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <Select value={targetId} onValueChange={setTargetId}>
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Select a workspace…" />
            </SelectTrigger>
            <SelectContent>
              {eligibleWorkspaces.length === 0 ? (
                <div className="px-3 py-6 text-sm text-muted-foreground text-center">
                  No other workspaces available.
                </div>
              ) : (
                eligibleWorkspaces.map((ws) => {
                  const Icon = ws.is_private ? User : Layers;
                  const label = ws.is_private ? "My Workspace" : ws.name;
                  return (
                    <SelectItem key={ws.id} value={ws.id} className="py-2.5">
                      <span className="flex items-center gap-2.5">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-foreground/[0.06] dark:bg-n-95/[0.06]">
                          <Icon className="h-3.5 w-3.5 text-foreground/60" />
                        </span>
                        <span className="text-[13px] font-medium truncate">
                          {label}
                        </span>
                      </span>
                    </SelectItem>
                  );
                })
              )}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleMove} disabled={!targetId || isBusy}>
            {isBusy ? "Moving…" : "Move"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
